import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import TextFieldInput from 'components/common/TextFieldInput/TextFieldInput';
import {
  validateEmailAddress,
  validateIsEmptyAndPhoneNumber,
  validateEmpty,
  validateName,
} from 'utils/validators';
import { IExperiencePayload } from 'features/ShoppingCart/types';
import useCartExperienceSettings from 'features/ShoppingCart/hooks/useCartExperienceSettings';
import { Sep, SrOnly } from 'components/common/styled/common-styled';
import PhoneNumberController from '../PhoneNumber/PhoneNumberController';

interface IPurchaserFieldProps {
  experiences: IExperiencePayload;
}

const PurchaserSection = ({ experiences }: IPurchaserFieldProps) => {
  const [telephoneCountryCode, setTelephoneCountryCode] = useState<string>('');
  const { t: translateText } = useTranslation();
  const { isPhoneRequired, isPhoneIncluded, isEmailIncluded, isEmailRequired } =
    useCartExperienceSettings(experiences);

  return (
    <Purchaser>
      <PurchaserFieldset>
        <SrOnly as="legend" className="page-heading">
          {translateText('purchaserInformation', 'Purchaser Information')}
        </SrOnly>
        <StyledRow>
          <TextFieldInput
            name="purchaser.firstName"
            label={`${translateText('firstName')}`}
            rules={{
              ...validateEmpty(),
              ...validateName('Please only use alphanumeric characters'),
            }}
            autoComplete="given-name"
            placeholder={translateText('firstName')}
            required
          />
          <TextFieldInput
            name="purchaser.lastName"
            label={`${translateText('lastName')}`}
            rules={{
              ...validateEmpty(),
              ...validateName('Please only use alphanumeric characters'),
            }}
            autoComplete="family-name"
            placeholder={translateText('lastName')}
            required
          />
        </StyledRow>
        <StyledRowResponsive>
          {isEmailIncluded && (
            <TextFieldInput
              name="purchaser.emailAddress"
              label={`${translateText('email')}`}
              rules={{
                ...(isEmailRequired && { ...validateEmpty() }),
                ...validateEmailAddress(),
              }}
              autoComplete="email"
              placeholder={translateText('email')}
              required
            />
          )}
          {isPhoneIncluded && (
            <PhoneNumberController
              autoComplete="tel"
              label={`${translateText('phone')} ${isPhoneRequired ? '*' : ''}`}
              name="purchaser.phoneNumber"
              placeholder={translateText('phone')}
              rules={{
                ...validateIsEmptyAndPhoneNumber(
                  isPhoneRequired,
                  telephoneCountryCode,
                ),
              }}
              onTelephoneCountryCodeChange={setTelephoneCountryCode}
              telephoneCountryCode={telephoneCountryCode}
            />
          )}
        </StyledRowResponsive>

        <Sep />
      </PurchaserFieldset>
    </Purchaser>
  );
};

const StyledRow = styled.div<{ singleField?: boolean }>`
  display: flex;
  margin: 0 -12px;

  > div {
    padding: 0 12px;
    width: 100%;
    width: ${({ singleField }) => (singleField ? '50%' : '100%')};
    margin-bottom: 20px;
  }
`;

const StyledRowResponsive = styled(StyledRow)`
  @media (max-width: 768px) {
    flex-direction: column;

    > div {
      width: 100%;
    }
  }
`;

const Purchaser = styled.div`
  margin-bottom: 42px;

  & > div:last-child {
    margin-top: 32px;
  }
`;

const PurchaserFieldset = styled.fieldset`
  border: none;
  margin: 0;
  padding: 0;
  width: 100%;
`;

export default PurchaserSection;
